import { useQuery } from "react-query";
import { sureApiCall } from "src/api/axiosClient/sureApi";
import { zodParseWithDetailedLog } from "src/api/apiErrorLogs";
import { DataListSchema } from "./useGetAllDataList";

export const dataListSelectorApiKey = "dataListSelector";

type UseDataListSelectorOptions = {
    searchText?: string;
    enabled?: boolean;
    itemsPerPage?: number;
};

export const useDataListSelector = (options: UseDataListSelectorOptions = {}) => {
    const { searchText = "", enabled = true, itemsPerPage = 200 } = options;

    const query = useQuery({
        queryKey: [dataListSelectorApiKey, searchText, itemsPerPage],
        queryFn: async () => {
            const params: Record<string, any> = {
                page: 1,
                itemsPerPage,
                order: { code: "ASC" },
            };

            // Add search parameter if searchText is provided
            // API Platform SearchFilter uses the property name as query parameter
            if (searchText && searchText.length > 0) {
                params.code = searchText;
            }

            const response = await sureApiCall.get<{ "hydra:member": unknown; "hydra:totalItems": number }>(
                "/api/data_lists",
                {
                    params,
                },
            );

            return response.data;
        },
        select: (data) => {
            if (!data || !data["hydra:member"]) {
                return {
                    dataLists: [],
                    totalItems: 0,
                };
            }

            const dataLists = data["hydra:member"] || [];
            const totalItems = data["hydra:totalItems"] || 0;

            const parsedDataLists = zodParseWithDetailedLog(dataLists, DataListSchema.array(), {
                operation: "GET",
                endpoint: "/data_lists",
            });

            return {
                dataLists: parsedDataLists,
                totalItems,
            };
        },
        enabled: enabled,
        staleTime: 30000, // Cache for 30 seconds
        cacheTime: 300000, // Keep in cache for 5 minutes
    });

    return {
        dataLists: query.data?.dataLists || [],
        totalItems: query.data?.totalItems || 0,
        ...query,
    };
};
